import { useOutletContext, useSearchParams } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type {
  PaymentIntentResult,
  PaymentRequest,
  PaymentRequestPaymentMethodEvent,
  SetupIntentResult,
} from '@stripe/stripe-js';
import type React from 'react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import useApplePay from '~/pages/upgrade/hooks/useApplePay';
import useFormState from '~/pages/upgrade/hooks/useFormState';
import useInternalCheckout from '~/pages/upgrade/hooks/useInternalCheckout';

import type { OutletContext } from '~/interfaces/outlet_context';

import useBoostAffiliate, { BOOST_AFFILIATE_COOKIE_NAME } from '~/hooks/useBoostAffiliate';
import useCreateStripePriceSession from '~/hooks/useCreateStripePriceSession';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';

import validateTaxId from '~/utils/validateTaxId';

import { useUpgradeContext } from '~/context/useUpgradeContext';
import useGiftFormCheckout from '~/pages/upgrade/hooks/useGiftFormCheckout';
import ActionForm from '../ActionForm';
import { useComponentFetcher } from '../ComponentErrorBoundary/useComponentFetcher';
import StatusInputs from '../Forms/StatusInputs';
import useGlobalModals from '../GlobalModals/hooks/useGlobalModals';
import type { ApplePayFetcherData } from './types';

type RenderProps = {
  isSubmitting: boolean;
  paymentRequest?: PaymentRequest;
  setEmail: (email: string) => void;
};

type Props = {
  children: (props: RenderProps) => ReactNode;
  canShowPaymentRequestButton: boolean;
  signupFlowId?: string;
  isCollectingBillingAddress?: boolean;
  onSuccess?: () => void;
};

const formatter = (currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
  });

const UpgradeAction = ({
  children,
  canShowPaymentRequestButton,
  signupFlowId,
  isCollectingBillingAddress,
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const premiumOfferIdParam = searchParams.get('offer_id') || undefined;
  const fetcher = useComponentFetcher<any>();
  const [hasRedirected, setHasRedirected] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessing3DSecure, setIsProcessing3DSecure] = useState(false);

  const elements = useElements();
  const stripe = useStripe();
  const {
    stripeSession,
    selectedTier,
    selectedPrice,
    selectedGiftPrice,
    isInternalCheckout,
    email,
    setEmail,
    taxId,
    donationAmountCents,
    setDonationAmountCents,
    setTaxId,
    currentFormMode,
  } = useUpgradeContext();
  const { setCookie, cookies, visitToken, referralId } = useOutletContext<OutletContext>();
  const boostAffiliateRecommendingPublicationId = useBoostAffiliate(cookies[BOOST_AFFILIATE_COOKIE_NAME] || undefined);
  const { handleSubmit } = useInternalCheckout();
  const { handleSubmit: handleGiftFormSubmit } = useGiftFormCheckout(isInternalCheckout);
  const { mutateAsync: createStripePriceSession } = useCreateStripePriceSession();
  const { nextPage, nextStep } = useSignupRedirects();
  const { handleOpenModal } = useGlobalModals();

  const { data: fetcherData, isSuccessful, isFailed } = useFormState<ApplePayFetcherData>(fetcher);

  const toastMessage = fetcherData?.toast?.message || '';

  const { paymentRequest, onPaymentRequestChange, applePayEvent, setApplePayEvent } = useApplePay({
    shouldCreatePaymentRequest: canShowPaymentRequestButton,
  });

  const validatedTaxId = validateTaxId(taxId);

  const handleClickSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsSubmitting(true);

    if (!isInternalCheckout) {
      if (selectedTier?.id === 'free') {
        // When user clicks on cta in a free tier price block
        // redirect to next step in signup flow (if in progress)
        // or signup modal
        if (nextStep) {
          nextPage();
          return;
        }
        handleOpenModal('signup');
        setIsSubmitting(false);
      } else if (!selectedPrice?.premium_offer || selectedPrice?.premium_offer.card_required) {
        if (!selectedPrice || !stripe) {
          setIsSubmitting(false);
          return;
        }

        try {
          const stripeSessionId = await createStripePriceSession(
            selectedPrice?.id,
            email,
            premiumOfferIdParam || selectedPrice?.premium_offer?.id,
            isCollectingBillingAddress,
          );

          if (stripeSessionId) {
            const response = await stripe.redirectToCheckout({
              sessionId: stripeSessionId,
            });

            if (response.error) {
              toast.error(
                response.error.message ||
                  'Something went wrong while creating a Stripe session, please refresh the page and try again',
              );
              setIsSubmitting(false);
            }
          } else {
            toast.error('Something went wrong while creating a Stripe session, please refresh the page and try again');
            setIsSubmitting(false);
          }
        } catch {
          toast.error('Something went wrong while creating a Stripe session, please refresh the page and try again');
          setIsSubmitting(false);
        }
      } else {
        // When user clicks on cta in a price block
        // but due to a premium offer we don't need them to
        // pay using a card
        searchParams.set('modal', 'signup');
        if (selectedPrice.premium_offer) {
          searchParams.set('premium_offer_id', selectedPrice.premium_offer.id);
        }
        if (signupFlowId) {
          searchParams.set('signup_flow_id', signupFlowId);
        }

        setSearchParams(searchParams);
        setIsSubmitting(false);
      }

      return;
    }

    if (selectedPrice?.interval === 'donation') {
      if (!donationAmountCents || Number.isNaN(donationAmountCents)) {
        toast.error('Donation amount must be a number');
        setIsSubmitting(false);
        return;
      }

      if (donationAmountCents < 100) {
        toast.error(`Amount must be at least ${formatter(selectedPrice.currency).format(1)}`);
        setIsSubmitting(false);
        return;
      }

      if (donationAmountCents > 100000) {
        toast.error(`Amount must be at most ${formatter(selectedPrice.currency).format(1000)}`);
        setIsSubmitting(false);
        return;
      }
    }

    if (!email) {
      toast.error('Email is required');
      setIsSubmitting(false);
      return;
    }

    if (taxId && !validatedTaxId.isValid) {
      toast.error('Invalid tax ID');
      setIsSubmitting(false);
      return;
    }

    if (!stripeSession || !elements) {
      // Stripe.js hasn't yet loaded.
      toast.error('Something went wrong loading Stripe, please refresh the page and try again');
      setIsSubmitting(false);
      return;
    }

    try {
      await handleSubmit(event, fetcher, stripeSession, elements, email);
    } catch (error: any) {
      toast.error(error.message || 'Something went wrong while creating a payment request, please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClickGiftFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsSubmitting(true);

    const formData = new FormData(event.currentTarget);
    const purchaserName = formData.get('purchaser_name');
    const recipientEmail = formData.get('recipient_email');

    if (!email) {
      toast.error('Email is required');
      setIsSubmitting(false);
      return;
    }

    if (!purchaserName) {
      toast.error('Your name is required');
      setIsSubmitting(false);
      return;
    }

    if (!recipientEmail) {
      toast.error("Recipient's email is required");
      setIsSubmitting(false);
      return;
    }

    if (isInternalCheckout && (!stripeSession || !elements)) {
      // Stripe.js hasn't yet loaded.
      toast.error('Something went wrong loading Stripe, please refresh the page and try again');
      setIsSubmitting(false);
      return;
    }

    try {
      await handleGiftFormSubmit(event, fetcher, stripeSession, elements, email);
    } catch (error: any) {
      toast.error(error.message || 'Something went wrong while creating a payment request, please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeApplePayEvent = (status: 'success' | 'fail') => {
    if (applePayEvent) {
      applePayEvent.complete(status);
      setApplePayEvent(null);
    }
  };

  const successMessage =
    currentFormMode === 'gift' ? t('upgrade.tabs.gift.success.message') : t('upgrade.success.message');

  const handleSuccess = () => {
    toast.success(toastMessage || successMessage);
    setIsSubmitting(false);
    setDonationAmountCents(0);
    setTaxId('');
    removeApplePayEvent('success');
    setHasRedirected(true);
    setCookie({ name: 'active_upgrade', value: 'true', days: 4 });
    setIsProcessing3DSecure(false);
    onSuccess?.();
  };

  const handleFail = (errorMessage?: string) => {
    removeApplePayEvent('fail');
    setCookie({ name: 'active_upgrade', value: 'true', days: 0 });
    setIsProcessing3DSecure(false);
    setIsSubmitting(false);
    toast.error(errorMessage || toastMessage || t('errors.default'));
  };

  const clientSecret = fetcherData?.clientSecret;
  const action = fetcherData?.action;

  const handleConfirmPayment = async (clientSecret: string) => {
    setIsProcessing3DSecure(true);

    const onConfirmComplete = (response?: SetupIntentResult | PaymentIntentResult) => {
      const setupIntentResponse = response as SetupIntentResult;
      const paymentIntentResponse = response as PaymentIntentResult;

      if (
        setupIntentResponse?.setupIntent?.status === 'succeeded' ||
        paymentIntentResponse?.paymentIntent?.status === 'succeeded'
      ) {
        handleSuccess();
        return;
      }

      if (setupIntentResponse?.error?.message || paymentIntentResponse?.error?.message) {
        handleFail(setupIntentResponse?.error?.message || paymentIntentResponse?.error?.message);
        return;
      }

      handleFail();
    };

    try {
      let response: any;
      if (action === 'requires_confirmation') {
        const cardElement = elements?.getElement(CardElement);
        if (cardElement) {
          response = await stripe?.confirmCardSetup(clientSecret, {
            payment_method: { card: cardElement },
          });
        }
      } else if (action === 'requires_action') {
        const cardElement = elements?.getElement(CardElement);
        if (cardElement) {
          response = await stripe?.confirmCardPayment(clientSecret, {
            payment_method: { card: cardElement },
          });
        }
      } else if (applePayEvent) {
        response = await stripe?.confirmCardPayment(clientSecret, {
          payment_method: applePayEvent.paymentMethod.id,
        });
      }

      onConfirmComplete(response);
    } catch (error) {
      Sentry.captureException(error);
      handleFail();
    }
  };

  // On Attempts that where additional authentication or actions by the user are required
  useEffect(() => {
    if (clientSecret && action) {
      handleConfirmPayment(clientSecret);
    }
  }, [clientSecret, action]);

  // On successful or failed attempts
  useEffect(() => {
    if (isSuccessful && !hasRedirected) {
      handleSuccess();
    } else if (isFailed) {
      handleFail();
    }
  }, [isSuccessful, isFailed, hasRedirected]);

  // Handles changes in payment request (Apple Pay)
  useEffect(() => {
    if (paymentRequest) {
      paymentRequest?.on('paymentmethod', (e: PaymentRequestPaymentMethodEvent) => {
        if (formRef.current) {
          onPaymentRequestChange(e, fetcher, formRef.current);
        }
      });
    }
  }, [paymentRequest, onPaymentRequestChange, fetcher]);

  return (
    <ActionForm
      formRef={formRef}
      fetcher={fetcher}
      method="post"
      action="/upgrade"
      onSubmit={currentFormMode === 'gift' ? handleClickGiftFormSubmit : handleClickSubmit}
    >
      <StatusInputs name="upgrade" errorMessage={t('errors.default')} successMessage={successMessage} />
      {currentFormMode === 'gift' && <input type="hidden" value="true" name="is_gift" />}
      <input name="sent_from_orchid" type="hidden" defaultValue="true" />
      {referralId && <input hidden name="ref" defaultValue={referralId} />}
      {boostAffiliateRecommendingPublicationId && (
        <input hidden name="bhba" defaultValue={boostAffiliateRecommendingPublicationId} />
      )}
      {visitToken && <input hidden name="visit_token" defaultValue={visitToken} />}
      {signupFlowId ? <input type="hidden" name="signup_flow_id" defaultValue={signupFlowId} /> : null}
      {isInternalCheckout ? (
        <>
          <input type="hidden" name="force_three_d_secure" defaultValue={'false'} />
          {validatedTaxId.isValid ? <input type="hidden" name="tax_id_type" value={validatedTaxId.type} /> : null}
        </>
      ) : null}
      {cookies.last_resource_guid && (
        <input hidden name="last_resource_guid" defaultValue={cookies.last_resource_guid} />
      )}
      {!isInternalCheckout && email && <input type="hidden" name="email" defaultValue={email} />}
      {currentFormMode === 'individual' && selectedPrice ? (
        <>
          <input type="hidden" name="price_id" defaultValue={selectedPrice.id} />
          {selectedPrice.premium_offer && (
            <input type="hidden" name="premium_offer_id" defaultValue={selectedPrice.premium_offer.id} />
          )}
          {selectedPrice?.interval === 'donation' && (
            <input type="hidden" name="amount_cents" defaultValue={donationAmountCents} />
          )}
        </>
      ) : null}
      {currentFormMode === 'gift' && selectedGiftPrice ? (
        <>
          <input type="hidden" name="price_id" defaultValue={selectedGiftPrice.id} />
          {selectedGiftPrice.premium_offer && (
            <input type="hidden" name="premium_offer_id" defaultValue={selectedGiftPrice.premium_offer.id} />
          )}
          {selectedGiftPrice?.interval === 'donation' && (
            <input type="hidden" name="amount_cents" defaultValue={donationAmountCents} />
          )}
        </>
      ) : null}
      {children({
        isSubmitting: isSubmitting || isProcessing3DSecure || fetcher.state === 'submitting',
        paymentRequest,
        setEmail,
      })}
    </ActionForm>
  );
};

export default UpgradeAction;
